import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { api } from "../../services/api";

type HttpMethod = "get" | "post" | "put" | "delete" | "patch";

type IndexBuildStatus = {
  success?: boolean;
  message?: string;
  status?: "idle" | "building" | "success" | "failed" | string;
  running?: boolean;
  startedAt?: string | null;
  finishedAt?: string | null;
  exitCode?: number | null;
  error?: string | null;
  lastOutput?: string;
};

function isIndexBuildStatus(value: unknown): value is IndexBuildStatus {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.status === "string" &&
    ("lastOutput" in v || "running" in v || "startedAt" in v || "exitCode" in v)
  );
}

function formatWhen(iso: string | null | undefined) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

const IndexBuilderPage: React.FC = () => {
  const [endpoint, setEndpoint] = useState("/backend/index/build");
  const [method, setMethod] = useState<HttpMethod>("post");
  const [payloadText, setPayloadText] = useState<string>('{\n  \n}');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<unknown>(null);
  const [copied, setCopied] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const pollTimerRef = useRef<number | null>(null);

  const payload = useMemo((): { value: unknown; error: string | null } => {
    const trimmed = payloadText.trim();
    if (!trimmed || trimmed === "{}") return { value: {}, error: null };
    try {
      return { value: JSON.parse(trimmed), error: null };
    } catch (e) {
      return {
        value: null,
        error: e instanceof Error ? e.message : "Invalid JSON payload",
      };
    }
  }, [payloadText]);

  const refreshStatus = async () => {
    try {
      const res = await api.request({ url: endpoint, method: "get" });
      setResult(res.data);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const status = e.response?.status;
        const msg =
          (typeof e.response?.data === "string" && e.response?.data) ||
          (e.response?.data as any)?.message ||
          e.message ||
          "Request failed";
        setError(status ? `${status}: ${msg}` : msg);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Request failed");
      }
    }
  };

  const sendRequest = async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setCopied(false);

    try {
      if (payload.error) throw new Error(`Invalid JSON: ${payload.error}`);
      const res = await api.request({
        url: endpoint,
        method,
        data:
          method === "get" || method === "delete" ? undefined : payload.value,
      });
      setResult(res.data);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const status = e.response?.status;
        const msg =
          (typeof e.response?.data === "string" && e.response?.data) ||
          (e.response?.data as any)?.message ||
          e.message ||
          "Request failed";
        setError(status ? `${status}: ${msg}` : msg);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Request failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const shouldPoll = useMemo(() => {
    if (!autoRefresh) return false;
    if (!isIndexBuildStatus(result)) return false;
    return Boolean(result.running) || result.status === "building";
  }, [autoRefresh, result]);

  useEffect(() => {
    if (!shouldPoll) {
      if (pollTimerRef.current != null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    if (pollTimerRef.current != null) return;
    pollTimerRef.current = window.setInterval(() => {
      void refreshStatus();
    }, 1500);

    return () => {
      if (pollTimerRef.current != null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPoll, endpoint]);

  const renderResponse = () => {
    if (isLoading) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-700">
          Loading response…
        </div>
      );
    }

    if (result == null) {
      return (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-sm text-gray-600">
          — No response yet
        </div>
      );
    }

    if (method === "get" && isIndexBuildStatus(result)) {
      const status = result.status ?? "unknown";
      const statusTone =
        status === "success"
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : status === "failed"
            ? "bg-red-50 text-red-700 border-red-200"
            : status === "building"
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-gray-50 text-gray-700 border-gray-200";

      const output = String(result.lastOutput ?? "");

      const copyOutput = async () => {
        try {
          await navigator.clipboard.writeText(output);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        } catch {
          setCopied(false);
        }
      };

      return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-gray-900">
                Index Build Status
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {result.message ? result.message : "Latest build details"}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${statusTone}`}
              >
                {status.toUpperCase()}
              </span>
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                  result.running
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-gray-50 text-gray-700 border-gray-200"
                }`}
              >
                {result.running ? "RUNNING" : "NOT RUNNING"}
              </span>
            </div>
          </div>

          {result.error ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {result.error}
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500">Started</div>
              <div className="text-sm font-medium text-gray-900 mt-1">
                {formatWhen(result.startedAt)}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500">Finished</div>
              <div className="text-sm font-medium text-gray-900 mt-1">
                {formatWhen(result.finishedAt)}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500">Exit Code</div>
              <div className="text-sm font-medium text-gray-900 mt-1">
                {result.exitCode ?? "—"}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500">Success</div>
              <div className="text-sm font-medium text-gray-900 mt-1">
                {result.success === true
                  ? "Yes"
                  : result.success === false
                    ? "No"
                    : "—"}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="text-sm font-semibold text-gray-900">Output</div>
              <button
                type="button"
                onClick={() => void copyOutput()}
                disabled={!output}
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="w-full overflow-auto rounded-2xl bg-gray-900 text-gray-100 p-4 text-xs max-h-96 whitespace-pre-wrap">
              {output ? output : "—"}
            </div>

            <details className="mt-4">
              <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">
                View raw JSON
              </summary>
              <pre className="mt-2 w-full overflow-auto rounded-2xl bg-gray-900 text-gray-100 p-4 text-xs">
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return (
      <pre className="w-full overflow-auto rounded-2xl bg-gray-900 text-gray-100 p-4 text-xs">
        {JSON.stringify(result, null, 2)}
      </pre>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Index Builder
              </h1>
              <p className="text-gray-600 mt-1">
                Uses the shared `api` client (`src/services/api.tsx`) to call
                backend endpoints.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endpoint
              </label>
              <input
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder="/backend/index/build"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              />
              <p className="text-xs text-gray-500 mt-1">
                Relative to `REACT_APP_API_URL` (or `http://localhost:5000/api`).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Method
              </label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as HttpMethod)}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              >
                <option value="post">POST</option>
                <option value="get">GET</option>
                <option value="put">PUT</option>
                <option value="patch">PATCH</option>
                <option value="delete">DELETE</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              JSON Payload
            </label>
            <textarea
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              rows={8}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              spellCheck={false}
            />
            <p className="text-xs text-gray-500 mt-1">
              Ignored for GET/DELETE. Invalid JSON will show an error on send.
            </p>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={() => void sendRequest()}
              disabled={isLoading}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? "Sending..." : "Send"}
            </button>

            <button
              type="button"
              onClick={() => void refreshStatus()}
              disabled={isLoading}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 font-medium hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              title="Fetch latest status via GET"
            >
              Refresh
            </button>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900/20"
              />
              Auto refresh
            </label>

            {error ? (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-xl flex-1">
                {error}
              </div>
            ) : null}
          </div>

          <div className="mt-6">
            <div className="text-sm font-semibold text-gray-900 mb-2">
              Response
            </div>
            {renderResponse()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndexBuilderPage;
